import axios from 'axios';
import { API_URL } from '../../utils/config';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Constantes para almacenamiento
export const TOKEN_KEY = '@MissionPlatform:token';
export const USER_KEY = '@MissionPlatform:user';

// Función para obtener URL base del backend
const getBaseUrl = () => {
  // Si API_URL incluye /api/v1, removerlo para obtener la base
  if (API_URL.includes('/api/v1')) {
    return API_URL.replace('/api/v1', '');
  }
  // Si API_URL incluye /api, removerlo
  if (API_URL.includes('/api')) {
    return API_URL.replace('/api', '');
  }
  // Si no incluye ninguno, asumir que es la URL base
  return API_URL;
};

// Crear instancia de axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Verificar token con el backend
export const verifyToken = async (token) => {
  if (!token) return false;
  
  try {
    const baseUrl = getBaseUrl();
    await axios.get(`${baseUrl}/api/auth/stats/`, {
      headers: { 'Authorization': `Token ${token}` }
    });
    return true;
  } catch (error) {
    // 403 = autenticado sin permiso → token válido igual
    if (error.response?.status === 403) return true;
    console.error('Error verificando token:', error.response?.status);
    return false;
  }
};

// Login
export const login = async (username, password) => {
  try {
    console.log('API URL:', API_URL);
    console.log('Intentando login con:', username);
    
    // Construir URL base del backend dinámicamente
    const baseUrl = getBaseUrl();
    console.log('Base URL calculada:', baseUrl);
    const loginUrl = `${baseUrl}/api/auth/login/`;
    console.log('URL de login:', loginUrl);
    
    const response = await axios.post(loginUrl, {
      username,
      password
    });
    
    console.log('Login exitoso:', response.data);
    
    // Guardar token y datos de usuario
    const { token, user_id, email, name, is_pilot, is_enterprise_user, is_staff, pilot_id, is_drone_operator, drone_operator_id } = response.data;
    
    // Guardar información completa del usuario
    const userData = { 
      id: user_id, 
      email,
      name: name || '',
      is_pilot: is_pilot || false,
      is_enterprise_user: is_enterprise_user || false,
      is_staff: is_staff || false,
      pilot_id: pilot_id || null,
      is_drone_operator: is_drone_operator || false,
      drone_operator_id: drone_operator_id || null,
    };
    
    // Guardar de forma síncrona para evitar problemas de timing
    await Promise.all([
      AsyncStorage.setItem(TOKEN_KEY, token),
      AsyncStorage.setItem(USER_KEY, JSON.stringify(userData))
    ]);
    
    // También guardar en localStorage para acceso desde servicios no-React
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Configurar el token para futuras solicitudes
    api.defaults.headers.common['Authorization'] = `Token ${token}`;
    
    return { token, user: userData };
  } catch (error) {
    console.error('Error durante login:', error);
    throw error;
  }
};

// Logout
export const logout = async () => {
  try {
    const token = await AsyncStorage.getItem(TOKEN_KEY);
    if (token) {
      // Configurar el token para la solicitud de logout
      api.defaults.headers.common['Authorization'] = `Token ${token}`;
      
      // Llamar al endpoint de logout
      const baseUrl = getBaseUrl();
      await axios.post(`${baseUrl}/api/auth/logout/`, {}, {
        headers: { 'Authorization': `Token ${token}` }
      });
    }
  } catch (error) {
    console.error('Error during logout API call:', error);
  } finally {
    // Limpiar token y datos de usuario localmente
    await AsyncStorage.removeItem(TOKEN_KEY);
    await AsyncStorage.removeItem(USER_KEY);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
  }
};

// Verificar si el usuario está autenticado
export const isAuthenticated = async () => {
  try {
    const token = await AsyncStorage.getItem(TOKEN_KEY);
    console.log('Token encontrado en almacenamiento:', !!token);
    
    if (!token) {
      return false;
    }
    
    // Verificar el token con el backend
    const isValid = await verifyToken(token);
    console.log('¿Token válido?', isValid);
    
    if (!isValid) {
      // Si el token no es válido, limpiar el almacenamiento
      await AsyncStorage.removeItem(TOKEN_KEY);
      await AsyncStorage.removeItem(USER_KEY);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error verificando autenticación:', error);
    return false;
  }
};

// Obtener el token actual
export const getToken = async () => {
  const asyncToken = await AsyncStorage.getItem(TOKEN_KEY);
  const localToken = localStorage.getItem('token');
  
  // Si no hay token en ninguno de los dos lugares
  if (!asyncToken && !localToken) {
    return null;
  }
  
  // Si solo hay token en uno de los lugares, sincronizar
  if (asyncToken && !localToken) {
    localStorage.setItem('token', asyncToken);
    return asyncToken;
  }
  
  if (!asyncToken && localToken) {
    await AsyncStorage.setItem(TOKEN_KEY, localToken);
    return localToken;
  }
  
  // Si hay inconsistencia, usar el más reciente (AsyncStorage tiene prioridad)
  if (asyncToken !== localToken) {
    console.log('Sincronizando tokens...');
    localStorage.setItem('token', asyncToken);
    return asyncToken;
  }
  
  return asyncToken;
};

// Obtener datos del usuario actual
export const getCurrentUser = async () => {
  try {
    const token = await AsyncStorage.getItem(TOKEN_KEY) || localStorage.getItem('token');
    if (token) {
      const baseUrl = getBaseUrl();
      // Timeout de 5s para no bloquear el login en conexiones lentas
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      try {
        const resp = await axios.get(`${baseUrl}/api/v1/me/`, {
          headers: { Authorization: `Token ${token}` },
          signal: controller.signal,
        });
        clearTimeout(timeout);
        const fresh = resp.data;
        const userData = {
          id: fresh.id,
          email: fresh.email,
          name: fresh.name || '',
          is_pilot: fresh.is_pilot || false,
          is_enterprise_user: fresh.is_enterprise_user || false,
          is_staff: fresh.is_staff || false,
          pilot_id: fresh.pilot_id || null,
          is_drone_operator: fresh.is_drone_operator || false,
          drone_operator_id: fresh.drone_operator_id || null,
        };
        await AsyncStorage.setItem(USER_KEY, JSON.stringify(userData));
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      } catch (fetchErr) {
        clearTimeout(timeout);
        // Si falla el fetch, caer al cache
      }
    }
  } catch (_) {}
  // Fallback al cache local
  const userStr = await AsyncStorage.getItem(USER_KEY) || localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

// Variable para evitar múltiples llamadas de logout
let isLoggingOut = false;

// Función para manejar logout automático
const handleAutoLogout = async () => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  
  console.log('Token expirado detectado, cerrando sesión automáticamente');
  
  try {
    // Limpiar almacenamiento local
    await AsyncStorage.removeItem(TOKEN_KEY);
    await AsyncStorage.removeItem(USER_KEY);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    
    // Disparar evento personalizado para que Redux maneje el logout
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:token-expired'));
    }
  } catch (error) {
    console.error('Error durante logout automático:', error);
  } finally {
    isLoggingOut = false;
  }
};

// Configurar token para solicitudes API (sin interceptor duplicado)
export const setupTokenInterceptor = async () => {
  const token = await AsyncStorage.getItem(TOKEN_KEY) || localStorage.getItem('token');
  if (token) {
    console.log('Configurando token para API:', token.substring(0, 10) + '...');
    api.defaults.headers.common['Authorization'] = `Token ${token}`;
    return true;
  }
  return false;
};

// Exportar la instancia de API para uso en otros servicios
export { api };